import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, TrendingUp, TrendingDown, Package, FileDown, 
  AlertTriangle, RefreshCw, Filter, ChevronDown, FileSpreadsheet,
  Clock, CheckCircle, XCircle, Archive
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency with proper locale and sign
const formatCurrency = (amount, currency = 'USD', showSign = false) => {
  if (amount === null || amount === undefined) return '—';
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.abs(amount));
  
  if (showSign && amount !== 0) {
    return amount >= 0 ? `+${formatted}` : `-${formatted}`;
  }
  return formatted;
};

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, attention, className = '' }) => (
  <Card className={`${attention ? 'border-amber-500/50' : ''} ${className}`}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium text-zinc-400">{title}</CardTitle>
      {Icon && <Icon className={`h-4 w-4 ${attention ? 'text-amber-500' : 'text-zinc-500'}`} />}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold text-white">{value}</div>
      {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
      {trend !== undefined && (
        <div className={`flex items-center mt-2 text-xs ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
          {trend >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
          <span>{trendValue}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Status Badge
const StatusBadge = ({ status, count }) => {
  const config = {
    draft: { color: 'bg-zinc-700 text-zinc-300', label: 'Draft' },
    available_for_private_sale: { color: 'bg-emerald-900/50 text-emerald-400', label: 'Available' },
    reserved: { color: 'bg-amber-900/50 text-amber-400', label: 'Reserved' },
    sold: { color: 'bg-blue-900/50 text-blue-400', label: 'Sold' },
    withdrawn: { color: 'bg-red-900/50 text-red-400', label: 'Withdrawn' },
    not_for_sale: { color: 'bg-purple-900/50 text-purple-400', label: 'Not for Sale' }
  };
  
  const { color, label } = config[status] || { color: 'bg-zinc-700', label: status };
  
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-zinc-400">{label}</span>
      <Badge className={`${color} min-w-[3rem] justify-center`}>{count}</Badge>
    </div>
  );
};

const InventoryDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const fetchWithAuth = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      if (response.status === 401) {
        navigate('/manage/login');
        throw new Error('Authentication required');
      }
      throw new Error(`Request failed: ${response.status}`);
    }
    return response;
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/api/collection-items/manage/inventory`;
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedStatus !== 'all') params.append('commercial_status', selectedStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetchWithAuth(url);
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedStatus, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleExport = async (exportType) => {
    setExporting(true);
    try {
      let url = `${API_URL}/api/collection-items/manage/inventory/export?export_type=${exportType}`;
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedStatus !== 'all') params.append('commercial_status', selectedStatus);
      if (params.toString()) url += `&${params.toString()}`;
      
      const response = await fetchWithAuth(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `inventory_${exportType}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="flex items-center gap-3 text-zinc-400">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span>Loading inventory dashboard...</span>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const summary = data?.summary || {};
  const attention = data?.attention_needed || {};
  const exchangeRate = data?.exchange_rate || {};
  const statusCounts = metrics.status_counts || {};

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ color: '#ffffff' }}>
              Financial Inventory Dashboard
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Private financial metrics and inventory management
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Filters */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="ticket">Ticket</SelectItem>
                <SelectItem value="design_mockup">Design Mockup</SelectItem>
                <SelectItem value="production_material">Production Material</SelectItem>
                <SelectItem value="document">Document</SelectItem>
                <SelectItem value="photograph">Photograph</SelectItem>
                <SelectItem value="memorabilia">Memorabilia</SelectItem>
                <SelectItem value="costume">Costume</SelectItem>
                <SelectItem value="award">Award</SelectItem>
                <SelectItem value="certification">Certification</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="available_for_private_sale">Available</SelectItem>
                <SelectItem value="reserved">Reserved</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
                <SelectItem value="withdrawn">Withdrawn</SelectItem>
                <SelectItem value="not_for_sale">Not for Sale</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchDashboard}
              disabled={loading}
              className="border-zinc-700"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Attention Alerts */}
        {(attention.missing_acquisition_data > 0 || attention.missing_images > 0 || attention.new_inquiries > 0) && (
          <div className="mb-6 p-4 bg-amber-900/20 border border-amber-900/50 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h3 className="font-medium text-amber-200">Items Need Attention</h3>
                <ul className="text-sm text-amber-300/80 space-y-1">
                  {attention.missing_acquisition_data > 0 && (
                    <li>• {attention.missing_acquisition_data} items missing acquisition data</li>
                  )}
                  {attention.missing_asking_price > 0 && (
                    <li>• {attention.missing_asking_price} available items missing asking price</li>
                  )}
                  {attention.missing_images > 0 && (
                    <li>• {attention.missing_images} items missing images</li>
                  )}
                  {attention.new_inquiries > 0 && (
                    <li>• {attention.new_inquiries} new inquiries to review</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard
            title="Total Artifacts"
            value={metrics.total_artifacts || 0}
            icon={Package}
            subtitle={`${summary.published_count || 0} published`}
          />
          <MetricCard
            title="Total Acquisition Cost"
            value={formatCurrency(metrics.total_acquisition_cost_usd || 0)}
            icon={DollarSign}
            subtitle="Purchase + all capitalized costs"
            attention={metrics.items_missing_acquisition_data > 0}
          />
          <MetricCard
            title="Unsold Asking Value"
            value={formatCurrency(metrics.unsold_asking_value_usd || 0)}
            icon={DollarSign}
            subtitle="Available + Reserved items"
          />
          <MetricCard
            title="Realized Gain/Loss"
            value={formatCurrency(metrics.total_realized_gain_loss_usd || 0, 'USD', true)}
            icon={metrics.total_realized_gain_loss_usd >= 0 ? TrendingUp : TrendingDown}
            subtitle="Sold items only"
            trend={metrics.total_realized_gain_loss_usd}
            trendValue={`${statusCounts.sold || 0} items sold`}
          />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Commercial Status */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Commercial Status</CardTitle>
              <CardDescription>Inventory by workflow stage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              <StatusBadge status="draft" count={statusCounts.draft || 0} />
              <StatusBadge status="available_for_private_sale" count={statusCounts.available_for_private_sale || 0} />
              <StatusBadge status="reserved" count={statusCounts.reserved || 0} />
              <StatusBadge status="sold" count={statusCounts.sold || 0} />
              <StatusBadge status="withdrawn" count={statusCounts.withdrawn || 0} />
              <StatusBadge status="not_for_sale" count={statusCounts.not_for_sale || 0} />
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Financial Summary</CardTitle>
              <CardDescription>
                All figures in USD • JPY rate: ¥{exchangeRate.usd_to_jpy?.toFixed(2) || '—'}
                {exchangeRate.is_stale && <span className="text-amber-500 ml-2">(stale)</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Acquisitions</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Total Acquisition Cost</span>
                      <span className="font-medium">{formatCurrency(summary.total_acquisition_cost_usd)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Appraised Value</span>
                      <span className="font-medium">{formatCurrency(summary.total_appraised_value_usd)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Asking Total</span>
                      <span className="font-medium">{formatCurrency(summary.total_asking_value_usd)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Sales</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Net Proceeds</span>
                      <span className="font-medium">{formatCurrency(metrics.total_net_proceeds_usd)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Realized Sales</span>
                      <span className="font-medium">{formatCurrency(summary.total_realized_sales_usd)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Gain/Loss</span>
                      <span className={`font-medium ${metrics.total_realized_gain_loss_usd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(metrics.total_realized_gain_loss_usd, 'USD', true)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Unrealized Spread Note */}
              <div className="mt-6 pt-4 border-t border-zinc-800">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-zinc-400">Unrealized Asking Spread</span>
                    <span className="text-xs text-zinc-600 block">Planning figure, not realized profit</span>
                  </div>
                  <span className="font-medium text-zinc-300">
                    {formatCurrency(metrics.unrealized_asking_spread_usd || summary.unrealized_asking_spread_usd)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Acquisition by Currency */}
        {metrics.acquisition_by_currency && metrics.acquisition_by_currency.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Acquisition Cost by Currency</CardTitle>
              <CardDescription>Original currency totals and normalized USD values</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {metrics.acquisition_by_currency.map((breakdown, idx) => (
                  <div key={idx} className="p-4 bg-zinc-900 rounded-lg">
                    <div className="text-lg font-bold">{breakdown.currency}</div>
                    <div className="text-sm text-zinc-400 mt-1">
                      {breakdown.item_count} items
                    </div>
                    <div className="mt-3 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Original Total</span>
                        <span>{formatCurrency(breakdown.original_total, breakdown.currency)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Normalized USD</span>
                        <span>{formatCurrency(breakdown.normalized_usd_total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Export Data</CardTitle>
            <CardDescription>
              Download inventory data as CSV. Current filters will be applied.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                onClick={() => handleExport('summary')}
                disabled={exporting}
                className="border-zinc-700"
                data-testid="export-summary-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export Summary
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport('full_financial')}
                disabled={exporting}
                className="border-zinc-700"
                data-testid="export-full-btn"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Export Full Financial
              </Button>
              {exporting && (
                <span className="text-sm text-zinc-500 flex items-center">
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Generating export...
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-600 mt-3">
              Full financial export includes all acquisition costs, buyer details, and transaction history.
              Summary export excludes sensitive details.
            </p>
          </CardContent>
        </Card>

        {/* Exchange Rate Info */}
        <div className="mt-6 text-xs text-zinc-600 flex items-center gap-2">
          <Clock className="h-3 w-3" />
          <span>
            Exchange rate: {exchangeRate.provider} • 
            Updated {exchangeRate.retrieved_at ? new Date(exchangeRate.retrieved_at).toLocaleString() : 'N/A'}
            {exchangeRate.is_stale && ' (stale - may need refresh)'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default InventoryDashboard;
